// flatBrush.js
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints, interpolatePointsUniform } from './helpers/points';
import { calculatePathDistances, calculatePath } from './helpers/points';

const defaultBrushSettings = brushDefaults.flat || {};

export async function drawOilFlatStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (points.length === 0) return;

  const brushSettings = Object.assign({}, defaultBrushSettings, sets);
  const {
    opacity = 1,
    wobble = 0.05,
    density = 1.0,
    flatness = 2.0,
    textureStrength = 0.2,

    bristleSizeMin = 0.25,
    bristleBreakup = 0.65,

    weight = 1.0,
    proportionalTail = false,
    tailLength = 2.5,
    tailRelativeLength = 0.4,

    edgeStep = 8,

    // Автоповорот/угол
    autoRotation = true,
    angle = 0,

    // Базовый масштаб
    baseScaleFactor = 30,
  } = brushSettings;

  const chromaColor = chroma(color);
  const originalAlpha = chromaColor.alpha();
  const baseBristleColor = gradientColor
    ? chroma('black').alpha(opacity).hex()
    : chromaColor.alpha(opacity).hex();

  // Предобработка точек и вычисление границ
  const { points: prePoints, bounds } = preprocessPointsFlat({
    points,
    lineWidth,
    tailLength,
  });
  if (!bounds || prePoints.length === 0) return;

  // Предварительная траектория с углами и taper
  const orientedPoints = precomputeTrajectoryFlat({
    points: prePoints,
    lineWidth,
    edgeStep,
    weight,
    proportionalTail,
    tailLength,
    tailRelativeLength,
    baseScaleFactor,
    autoRotation,
    angle,
  });

  // Offscreen буфер
  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const bufferCtx = bufferCanvas.getContext('2d');

  const isSmall = lineWidth < baseScaleFactor;
  let bristleCount = Math.round(400 * density);
  let bristleWidth = lineWidth / 30;

  // Консистентность кисти на малых размерах
  if (isSmall) {
    const densityScale = Math.pow(lineWidth / baseScaleFactor, 1.3);
    bristleCount = Math.max(2, Math.round(bristleCount * densityScale));
    const minBristleWidth = (baseScaleFactor / 30) * 0.75;
    bristleWidth = Math.max(bristleWidth, minBristleWidth);
  }

  const wobbleAmount = lineWidth * wobble;

  // Генерируем офсеты ворсинок эллиптической пачкой
  const offsets = generateEllipticOffsets(time, bristleCount, lineWidth, flatness);

  bufferCtx.lineCap = 'round';
  bufferCtx.lineJoin = 'round';

  // Группировка офсетов по цвету + подготовка функции вариации ширины
  const colorGroups = groupOffsetsByColorFlat({
    offsets,
    chromaColor,
    opacity,
    time,
    textureStrength,
    baseBristleColor: gradientColor ? baseBristleColor : null,
  });

  // Новая схема: заранее генерируем все сегменты для всех ворсинок,
  // группируем их по (color + widthBucket) и рисуем минимальным количеством stroke.
  // widthBucket — чтобы не плодить уникальные lineWidth на каждый пиксель, но сохранить вариативность.
  const segmentBatches = new Map(); // key = `${color}::${widthBucket}`, value = array of polylines (each polyline = array of points with quadratic midpoints)

  // Настраиваем разбиение ширины на дискретные бакеты для редукции количества stroke-вызовов
  // Чем больше buckets, тем менее агрессивна оптимизация, но выше визуальная точность.
  const WIDTH_BUCKETS = 8;

  const addPolylineToBatch = (colorKey, widthKey, polyline) => {
    if (!polyline || polyline.length < 2) return;
    const key = `${colorKey}::${widthKey}`;
    let arr = segmentBatches.get(key);
    if (!arr) {
      arr = [];
      segmentBatches.set(key, arr);
    }
    arr.push(polyline);
  };

  // Для каждой ворсинки создаём детерминированную последовательность «видимых» сегментов без соплей:
  // - любой пропуск (по breakup/тэппер/pressure) завершает текущую полилинию;
  // - переключение ворсинки — автоматический обрыв, потому что полилинии не смешиваются между волосками.
  Object.entries(colorGroups).forEach(([groupColor, groupOffsets]) => {
    groupOffsets.forEach(({ offset, varyWidth }) => {
      // Вариативность ширины ворсинки, но затем будет дискретизация в бакеты
      const variedBristleWidth = varyWidth(bristleWidth);

      // Детерминированное зерно для пропусков по ворсинке
      const offsetSeed = offset.seed;
      const deterministicPart = seededRandom(offsetSeed * 3.14);

      // Собираем текущую полилинию (кривая) как последовательность команд:
      // Мы будем использовать квадратичные сегменты (mid-поинт), но хранить как точки для простоты и скорости.
      let currentPolyline = null;

      for (let i = 1; i < orientedPoints.length; i++) {
        const prevPoint = orientedPoints[i - 1];
        const currPoint = orientedPoints[i];

        // Усреднённый taper для сегмента
        const averageTaper = (prevPoint.taper + currPoint.taper) / 2;

        // Если taper почти 0 — это «невидимая» часть хвоста: обрываем
        if (averageTaper < 0.01) {
          if (currentPolyline && currentPolyline.length > 1) {
            const widthForSeg = computeTaperedWidth(variedBristleWidth, averageTaper, bristleSizeMin);
            const widthBucket = widthToBucket(widthForSeg, WIDTH_BUCKETS);
            addPolylineToBatch(groupColor, widthBucket, currentPolyline);
          }
          currentPolyline = null;
          continue;
        }

        // Псевдорандомный пропуск: зависит только от ворсинки (offsetSeed) и индекса i + time,
        // но детерминирован так, чтобы при удлинении штриха прошлые значения не менялись.
        const baseSkipChance = (1 - averageTaper) * 0.9;
        const randomPart = seededRandom(hash3(time, i, offsetSeed));
        const combinedRandom = deterministicPart * (1 - bristleBreakup) + randomPart * bristleBreakup;
        const shouldSkip = combinedRandom < baseSkipChance;

        if (shouldSkip) {
          if (currentPolyline && currentPolyline.length > 1) {
            const widthForSeg = computeTaperedWidth(variedBristleWidth, averageTaper, bristleSizeMin);
            const widthBucket = widthToBucket(widthForSeg, WIDTH_BUCKETS);
            addPolylineToBatch(groupColor, widthBucket, currentPolyline);
          }
          currentPolyline = null;
          continue;
        }

        // Геометрия сегмента с учётом нормали/тангенса, офсета и wobble
        const angle = currPoint.angle ?? (prevPoint.angle ?? 0);
        const nx = Math.cos(angle + Math.PI / 2);
        const ny = Math.sin(angle + Math.PI / 2);
        const tx = Math.cos(angle);
        const ty = Math.sin(angle);

        const effOffsetX = nx * offset.x + tx * (offset.y ?? 0);
        const effOffsetY = ny * offset.x + ty * (offset.y ?? 0);

        const pressure = currPoint.pressure ?? 1;
        const adjustedWobble = wobbleAmount * (1 - pressure * 0.5);
        const wobbleX = (seededRandom(hash3(time, offsetSeed, i + 200)) - 0.5) * adjustedWobble;
        const wobbleY = (seededRandom(hash3(time, offsetSeed, i + 300)) - 0.5) * adjustedWobble;

        // Квадратичный сегмент (используем mid-point сглаживание)
        const prevX = prevPoint.x + effOffsetX;
        const prevY = prevPoint.y + effOffsetY;
        const midX = (prevPoint.x + currPoint.x) / 2 + effOffsetX + wobbleX;
        const midY = (prevPoint.y + currPoint.y) / 2 + effOffsetY + wobbleY;

        // Открываем новую полилинию при необходимости
        if (!currentPolyline) {
          currentPolyline = [];
          currentPolyline.push({ x: prevX, y: prevY, cx: prevX, cy: prevY, isMove: true });
        }

        // Пишем сегмент
        currentPolyline.push({ x: midX, y: midY, cx: prevX, cy: prevY, isMove: false });

        // Если следующий шаг будет обрываться, мы закроем в следующей итерации по логике выше
        // либо закрытие произойдет в конце ворсинки
        if (i === orientedPoints.length - 1) {
          const widthForSeg = computeTaperedWidth(variedBristleWidth, averageTaper, bristleSizeMin);
          const widthBucket = widthToBucket(widthForSeg, WIDTH_BUCKETS);
          addPolylineToBatch(groupColor, widthBucket, currentPolyline);
          currentPolyline = null;
        }
      }
    });
  });

  // Теперь рисуем все батчи с минимальным количеством .stroke:
  // Каждый батч имеет единый цвет и единую дискретизированную толщину.
  for (const [key, polylines] of segmentBatches.entries()) {
    const [strokeStyle, widthBucketStr] = key.split('::');
    const width = bucketToWidth(widthBucketStr, bristleWidth, WIDTH_BUCKETS);

    bufferCtx.strokeStyle = strokeStyle;
    bufferCtx.lineWidth = Math.max(0.1, width);

    bufferCtx.beginPath();
    // Каждую полилинию добавляем в один общий path, но с обязательным moveTo при начале
    for (let p = 0; p < polylines.length; p++) {
      const poly = polylines[p];
      if (!poly || poly.length < 2) continue;

      // Гарантируем отсутствие «соплей»: каждая полилиния начинается с moveTo
      for (let i = 0; i < poly.length; i++) {
        const seg = poly[i];
        if (seg.isMove) {
          bufferCtx.moveTo(seg.x, seg.y);
        } else {
          // quadraticCurveTo: контрольная точка = cx, cy, конечная = x, y
          bufferCtx.quadraticCurveTo(seg.cx, seg.cy, seg.x, seg.y);
        }
      }
    }
    bufferCtx.stroke();
  }

  // Градиентная заливка поверх мазка
  if (gradientColor) {
    fillGradient(bufferCtx, orientedPoints, color, gradientColor, lineWidth);
    context.globalAlpha = 1;
  } else {
    context.globalAlpha = originalAlpha;
  }

  // Рендерим буфер на основной контекст
  context.drawImage(
    bufferCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
  context.globalAlpha = 1;
}

/* -------------------------- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ -------------------------- */

function seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Детерминированный хэш-комбайнер для стабильного псевдорандома по (time, i, offsetSeed)
function hash3(a, b, c) {
  // Простая комбинация, стабильная в JS double
  const s = Math.sin(a * 12.9898 + b * 78.233 + c * 37.719) * 43758.5453;
  return s - Math.floor(s);
}

function addPathAnglesWithEdgeSkipping(points, { edgeStep = 4, totalPathLength }) {
  if (!points || points.length === 0) return points;
  const n = points.length;
  const out = new Array(n);
  const clampIndex = (idx) => Math.max(0, Math.min(n - 1, idx));

  for (let i = 0; i < n; i++) {
    const p = points[i];
    const distanceFromEnd = totalPathLength - p.distance;

    const isInStartWindow = p.distance < distanceFromEnd && p.distance < 20;
    const isInEndWindow = p.distance > distanceFromEnd && distanceFromEnd < 20;

    let dx = 0, dy = 0;

    if (isInStartWindow) {
      const j = clampIndex(i + edgeStep);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    } else if (isInEndWindow) {
      const j = clampIndex(i - edgeStep);
      dx = p.x - points[j].x;
      dy = p.y - points[j].y;
    } else {
      const j = clampIndex(i + 1);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    }
    const angle = Math.atan2(dy, dx);
    out[i] = { ...p, angle };
  }
  return out;
}

function generateEllipticOffsets(time, count, radius, flatness) {
  const a = radius / 2;
  const b = radius / (2 + flatness * flatness);
  const arr = [];
  for (let i = 0; i < count; i++) {
    const r = Math.sqrt(seededRandom(time + i * 17.11));
    const theta = seededRandom(time + i * 9.73) * Math.PI * 2;
    const x = r * Math.cos(theta) * a;
    const y = r * Math.sin(theta) * b;
    const distance = Math.sqrt(x * x + y * y);
    const edge = Math.min(1, Math.sqrt((x * x) / (a * a) + (y * y) / (b * b)));
    arr.push({
      x, y, distance, edge,
      normalizedDistance: edge,
      seed: i + 1,
    });
  }
  arr.sort((a, b) => a.distance - b.distance);
  return arr;
}

function computeTaperedWidth(baseWidth, averageTaper, bristleSizeMin) {
  const taperedWidth = baseWidth * (bristleSizeMin + (1 - bristleSizeMin) * averageTaper);
  return Math.max(0.1, taperedWidth);
}

// Дискретизация ширины в бакеты для сокращения количества stroke вызовов
function widthToBucket(width, bucketCount) {
  // Лог-шкала или линейная. Оставим линейную вокруг базовой: нормализуем по ожидаемой ширине.
  // Для устойчивости просто квантуем до bucketCount уровней в диапазоне [0.1, 4*width] в относительных единицах.
  // Но так как нам приходит уже локальный width, квантуем до фиксированного шага:
  const step = 0.2; // 0.2 px шаг — хороший баланс. Можно параметризовать.
  const quant = Math.max(0.1, Math.round(width / step) * step);
  return quant.toFixed(2); // строковый ключ
}

function bucketToWidth(widthBucketStr, fallbackWidth, bucketCount) {
  const w = parseFloat(widthBucketStr);
  if (Number.isFinite(w)) return w;
  return fallbackWidth;
}

function groupOffsetsByColorFlat({
  offsets,
  chromaColor,
  opacity,
  time,
  textureStrength,
  baseBristleColor,
}) {
  const colorGroups = {};
  offsets.forEach(offset => {
    let currentBristleColor = baseBristleColor;

    if (!baseBristleColor) {
      // Текстурная вариативность цвета — детерминированно от офсета и времени
      const variation = seededRandom(time + offset.x + offset.y) * textureStrength * 4 - (textureStrength * 8 / 3);
      currentBristleColor = chroma(chromaColor).brighten(variation).alpha(opacity / 2).hex();
    }

    const varyWidth = (w) => w * (0.8 + seededRandom(time + (offset.distance || 0)) * 0.4);
    if (!colorGroups[currentBristleColor]) {
      colorGroups[currentBristleColor] = [];
    }
    colorGroups[currentBristleColor].push({ offset, varyWidth });
  });
  return colorGroups;
}

function preprocessPointsFlat({ points, lineWidth, tailLength }) {
  if (points.length === 0) return { points: [], bounds: null };
  const bounds = getStrokeBounds(points, lineWidth);
  if (!bounds) return { points: [], bounds: null };
  const offsetX = -bounds.x;
  const offsetY = -bounds.y;
  let pts = translatePoints(points, offsetX, offsetY);

  let uniformStep = Math.max(3, lineWidth / 5);
  if (pts[0]?.pressure) { uniformStep = 5; }
  pts = interpolatePointsUniform(pts, uniformStep);

  return { points: pts, bounds };
}

function precomputeTrajectoryFlat({
  points,
  lineWidth,
  edgeStep,
  weight,
  proportionalTail,
  tailLength,
  tailRelativeLength,
  baseScaleFactor,
  autoRotation,
  angle,
}) {
  const n = points.length;
  if (n === 0) return [];

  const totalPathLength = calculatePath(points);
  const pathPoints = calculatePathDistances(points);

  const taperedPoints = pathPoints.map((point) => {
    const fromStart = point.distance;
    const fromEnd = totalPathLength - point.distance;
    const minD = Math.min(fromStart, fromEnd);

    let baseTaper = 1.0 * weight;
    let tailEffectLength;

    if (proportionalTail) {
      tailEffectLength = totalPathLength * tailRelativeLength;
    } else {
      tailEffectLength = Math.max(lineWidth, baseScaleFactor) * tailLength;
    }

    tailEffectLength = Math.min(tailEffectLength, totalPathLength);

    if (tailEffectLength > 0 && minD < tailEffectLength) {
      const t = minD / tailEffectLength;
      const smoothedTaper = t * t * (3 - 2 * t);
      baseTaper *= smoothedTaper;
    }

    const finalTaper = point.pressure != null ? point.pressure : baseTaper;

    return {
      ...point,
      taper: Math.max(0, Math.min(1, finalTaper)),
      angle: 0,
      pressure: point.pressure,
    };
  });

  if (autoRotation) {
    return addPathAnglesWithEdgeSkipping(taperedPoints, {
      edgeStep,
      totalPathLength,
    });
  } else {
    const fixedAngleRad = angle * (Math.PI / 180);
    return taperedPoints.map(p => ({
      ...p,
      angle: fixedAngleRad,
    }));
  }
}
